import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, inject } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../../../../../../projects/my-app/src/environments/environment';
import { combineLatest, filter, Subscription } from 'rxjs';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { DashboardTitleService } from '../../shared-services/dashboard-title.service';
import { ThemeSelectorComponent } from '../theme-selector/theme-selector.component';
import { ThemeService } from '../../shared-services/theme.service';
import { LayoutService } from '../../shared-services/layout.service';
import { SlideshowService } from '../../shared-services/slideshow.service';

@Component({
  selector: 'app-side-navbar',
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatTooltipModule,
    RouterModule,
  ],
  templateUrl: './side-navbar.component.html',
  styleUrl: './side-navbar.component.scss',
})
export class SideNavbarComponent implements OnDestroy {
  private static readonly BREADCRUMB_STORAGE_KEY = 'dvm.nav.breadcrumbs';
  private static readonly BREADCRUMB_ACTIVE_FLOW_STORAGE_KEY = 'dvm.nav.activeFlowKey';
  private static readonly BREADCRUMB_ROUTE_STATE_STORAGE_KEY = 'dvm.nav.routeState';
  /** Sidebar drag-resize bounds + persistence key. Min/max chosen so the
   *  menu icons + labels stay readable at min and don't crowd content at max. */
  private static readonly SIDEBAR_MIN_WIDTH = 200;
  private static readonly SIDEBAR_MAX_WIDTH = 480;
  private static readonly SIDEBAR_DEFAULT_WIDTH = 280;
  private static readonly SIDEBAR_WIDTH_STORAGE_KEY = 'dvm.sidebar.expandedWidth';

  isExpanded = true;
  showSubmenu: boolean = false;
  isShowing = false;
  showSubSubMenu: boolean = false;
  isCollapsed = true;
  isMobileLayout = false;
  isMobileDrawerOpen = false;
  username!: string;
  logo!: string;
  companyText = environment.COMPANY_LOGO_TEXT;
  secondaryLogo!: string;
  clientLogo: string = '';
  clientIcon: string = '';
  clientText: string = '';
  hasClientLogo: boolean = false;
  useAltSidenav: boolean = false;
  altToolbarBg = '';
  altSidebarBg = '';

  /** Width (px) used when the sidebar is expanded and not in mobile drawer
   *  mode. Hydrated from localStorage on init; updated live during drag. */
  sidebarExpandedWidth = SideNavbarComponent.SIDEBAR_DEFAULT_WIDTH;
  /** True while the user is actively dragging the resize handle. Suppresses
   *  the sidebar's width transition so width tracks the cursor 1:1. */
  isResizingSidebar = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private resizePointerId: number | null = null;

  // Light/Dark theme toggle
  private themeService = inject(ThemeService);
  private layoutService = inject(LayoutService);
  private slideshowService = inject(SlideshowService);
  private themeSub?: Subscription;
  private pendingPreviewSub?: Subscription;
  private slideshowStateSub?: Subscription;
  private slideshowProgressSub?: Subscription;
  private dashboardTitleSub?: Subscription;
  isDarkTheme = false;
  dashboardTitle = '';
  dashboardTitlePosition: 'top' | 'below' | 'default' = 'default';
  dashboardTitleFontSize = '';
  breadcrumbItems: Array<{ route: string; configFile: string; label: string }> = [];
  breadcrumbFlowEnabled = false;
  showThemeOptions = true;
  showThemeToggle = true;

  /** Drives the toolbar "Save Theme" button visibility — true after the user
   * hits Apply & Preview in the Theme Options dialog and not yet committed. */
  hasPendingThemePreview = false;

  /** Mirrored slideshow state — toolbar template binds to these for the
   *  inline Play/Pause/Apply/Save/Stop controls. Plain fields rather than
   *  async pipes so multiple template bindings on the same state field
   *  don't open separate subscriptions. */
  slideshowActive = false;
  slideshowPlaying = false;
  slideshowCurrentName = '';
  slideshowIndex = 0;
  slideshowTotal = 0;

  /** 0–1 progress through the current slide cycle. Bound to the progress
   *  bar's width %; updated each animation frame while playing. */
  slideshowProgress = 0;

  /** Live ref to the Theme Options dialog so re-clicking the toolbar button
   * while the dialog is already open is a no-op instead of stacking dialogs. */
  private themeSelectorDialogRef: MatDialogRef<ThemeSelectorComponent> | null = null;

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  /** Persist the previewed theme + customs + layout from the toolbar Save
   *  button. The single Save covers all three pending states so the user
   *  doesn't have to click multiple buttons after previewing changes. */
  saveThemePreview(): void {
    this.themeService.commitPendingPreview();
    this.layoutService.commitPendingLayout();
  }

  // ── Slideshow toolbar actions (delegate to SlideshowService) ─────

  /** Toggle the autoplay timer; the dashboard keeps showing the current
   *  slide either way. */
  toggleSlideshowPlayback(): void {
    if (this.slideshowPlaying) {
      this.slideshowService.pause();
    } else {
      this.slideshowService.play();
    }
  }

  slideshowNext(): void {
    this.slideshowService.next();
  }

  slideshowPrev(): void {
    this.slideshowService.prev();
  }

  /** "Apply & Preview" from the toolbar — keep the current slide as a
   *  pending preview (Save Theme button picks it up), end the slideshow. */
  slideshowApply(): void {
    this.slideshowService.applyAndStop();
  }

  /** Commit current slide to localStorage immediately + end. */
  slideshowSave(): void {
    this.slideshowService.saveAndStop();
  }

  /** Stop and revert to the theme that was active before Play was hit. */
  slideshowStop(): void {
    this.slideshowService.cancelAndStop();
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.pendingPreviewSub?.unsubscribe();
    this.slideshowStateSub?.unsubscribe();
    this.slideshowProgressSub?.unsubscribe();
    this.dashboardTitleSub?.unsubscribe();
    // If the user navigates away mid-drag the window listeners would leak
    // and keep firing on a destroyed component — clean them up here.
    if (this.isResizingSidebar) this.endSidebarResize(false);
  }

  /** Begin a drag-resize on the sidebar's right edge. No-op on mobile (the
   *  drawer is full-width) or when collapsed (handle isn't rendered). */
  startSidebarResize(event: PointerEvent): void {
    if (this.isMobileLayout || this.isCollapsed) return;
    event.preventDefault();
    event.stopPropagation();
    this.resizePointerId = event.pointerId;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.sidebarExpandedWidth;
    this.isResizingSidebar = true;
    window.addEventListener('pointermove', this.onResizePointerMove);
    window.addEventListener('pointerup', this.onResizePointerUp);
    window.addEventListener('pointercancel', this.onResizePointerUp);
  }

  private onResizePointerMove = (event: PointerEvent): void => {
    if (this.resizePointerId !== event.pointerId) return;
    const delta = event.clientX - this.resizeStartX;
    const next = this.clampSidebarWidth(this.resizeStartWidth + delta);
    if (next !== this.sidebarExpandedWidth) {
      this.sidebarExpandedWidth = next;
    }
  };

  private onResizePointerUp = (event: PointerEvent): void => {
    if (this.resizePointerId !== event.pointerId) return;
    this.endSidebarResize(true);
  };

  /** Tear down the drag — optionally persist the new width and trigger the
   *  same staged reflow used by collapse/expand so chart widgets resize. */
  protected endSidebarResize(commit: boolean): void {
    this.isResizingSidebar = false;
    this.resizePointerId = null;
    window.removeEventListener('pointermove', this.onResizePointerMove);
    window.removeEventListener('pointerup', this.onResizePointerUp);
    window.removeEventListener('pointercancel', this.onResizePointerUp);
    if (commit) {
      this.persistSidebarWidth();
      this.scheduleDesktopReflow();
    }
  }

  protected clampSidebarWidth(width: number): number {
    return Math.min(
      SideNavbarComponent.SIDEBAR_MAX_WIDTH,
      Math.max(SideNavbarComponent.SIDEBAR_MIN_WIDTH, Math.round(width)),
    );
  }

  protected persistSidebarWidth(): void {
    try {
      localStorage.setItem(
        SideNavbarComponent.SIDEBAR_WIDTH_STORAGE_KEY,
        String(this.sidebarExpandedWidth),
      );
    } catch {
      /* ignore */
    }
  }

  protected loadSidebarWidth(): void {
    try {
      const raw = localStorage.getItem(
        SideNavbarComponent.SIDEBAR_WIDTH_STORAGE_KEY,
      );
      if (!raw) return;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) return;
      this.sidebarExpandedWidth = this.clampSidebarWidth(parsed);
    } catch {
      /* ignore */
    }
  }

  /**
   * Select logos based on environment flags.
   * Use Case 1: CLIENT_LOGO_SRC empty → show default SIM Advisory branding (logo + company text).
   * Use Case 2: CLIENT_LOGO_SRC provided → show client logo alone.
   * Colors (toolbar/sidebar bg + text) are controlled by ThemeService via CSS variables.
   */
  protected initLogos(): void {
    const env = environment as any;
    const useAlt = env.USE_ALT_SIDENAV === true || env.USE_ALT_SIDENAV === 'true';
    this.useAltSidenav = useAlt;
    this.altToolbarBg = (env.SECONDARY_COLOR || '').trim();
    this.altSidebarBg = (env.SECONDARY_COLOR_2 || '').trim();

    this.showThemeOptions = this.isEnabled(env.THEME_OPTIONS_ENABLED);
    this.showThemeToggle = this.isEnabled(env.THEME_TOGGLE_ENABLED);


    const clientSrc = (env.CLIENT_LOGO_SRC || '').trim();
    const clientIcon = (env.CLIENT_LOGO_ICON || '').trim();
    const clientText = (env.CLIENT_LOGO_TEXT || '').trim();
    this.clientLogo = clientSrc;
    this.clientIcon = clientIcon;
    this.clientText = clientText;
    this.hasClientLogo = !!clientSrc || !!clientIcon;

    if (useAlt) {
      this.logo = env.ALT_COMPANY_LOGO_SRC;
      this.secondaryLogo = env.ALT_SECONDARY_LOGO_SRC;
    } else {
      // Prefer DEFAULT_COMPANY_LOGO (local SVG) over COMPANY_LOGO_SRC (remote PNG) when set.
      const defaultLogo = (env.DEFAULT_COMPANY_LOGO || '').trim();
      this.logo = defaultLogo || environment.COMPANY_LOGO_SRC;
      // Prefer DEFAULT_MAKE_INDIA_LOGO (local SVG) over SECONDARY_LOGO_SRC (remote PNG) when set.
      const defaultMakeIndia = (env.DEFAULT_MAKE_INDIA_LOGO || '').trim();
      this.secondaryLogo = defaultMakeIndia || environment.SECONDARY_LOGO_SRC;
    }
  }

  private isEnabled(value: boolean | string | undefined): boolean {
    if (value === undefined || value === null || value === '') {
      return true;
    }
    return value === true || String(value).trim().toLowerCase() === 'true';
  }

  menuItems: any[] = [];
  breadcrumbFlows: Array<{
    key: string;
    enabled: boolean;
    items: Array<{
      name: string;
      path: string;
      configFile: string;
      backNavigation?: {
        requestParamKeys?: string[];
        queryParamKeys?: string[];
        navigationRequestParamKeys?: string[];
        navigationFilterListener?: string[];
        navigationSourceWidgetId?: string;
        valueSource?: 'current' | 'target' | 'merged';
        payloadMode?: 'mapped' | 'savedState';
      } | null;
    }>
  }> = [];
  expandedMenus: Set<string> = new Set();
  isChatRoute: boolean = false;
  // Macro panel state
  isMacroPanelOpen = false;
  isLoadingMacros = false;
  isLoadingMacroContent = false;
  macros: string[] = [];
  macroErrorMessage: string | null = null;

  constructor(
    private http: HttpClient,
    private keycloakService: KeycloakService,
    private router: Router,
    private responsiveLayoutService: ResponsiveLayoutService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private dashboardTitleService: DashboardTitleService,
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.isChatRoute = event.urlAfterRedirects.includes('/chat');
        this.syncBreadcrumbs();
        if (this.isMobileLayout) {
          this.isMobileDrawerOpen = false;
        }
      });
  }


  async ngOnInit(): Promise<void> {
    this.breakpointObserver.observe(['(max-width: 991.98px)']).subscribe((state) => {
      this.isMobileLayout = state.matches;
      this.isMobileDrawerOpen = false;
    });

    this.initLogos();
    this.loadSidebarWidth();
    this.loadBreadcrumbs();
    this.syncBreadcrumbs();
    // Track active theme so the toolbar toggle icon reflects light/dark state.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      this.isDarkTheme = this.themeService.currentThemeName === 'dark';
    });
    // Show/hide the toolbar "Save Theme" button based on pending preview
    // state — true if EITHER the theme/custom OR the custom layout is in a
    // previewed-but-not-saved state. A single Save commits whichever is pending.
    this.pendingPreviewSub = combineLatest([
      this.themeService.pendingPreview$,
      this.layoutService.pendingLayout$,
    ]).subscribe(([themePending, layoutPending]) => {
      this.hasPendingThemePreview = themePending || layoutPending;
    });
    // Mirror the slideshow service state into local fields so the toolbar
    // template can show controls (play/pause/prev/next/apply/save/stop) and
    // the progress bar without juggling multiple async pipes — the service
    // is a singleton, so subscribing once here is enough.
    this.slideshowStateSub = this.slideshowService.state$.subscribe((s) => {
      this.slideshowActive = s.active;
      this.slideshowPlaying = s.playing;
      this.slideshowIndex = s.index;
      this.slideshowTotal = s.items.length;
      this.slideshowCurrentName = s.current?.name || '';
    });
    this.slideshowProgressSub = this.slideshowService.progress$.subscribe((p) => {
      this.slideshowProgress = p;
    });
    this.dashboardTitleSub = this.dashboardTitleService.state$.subscribe((state) => {
      this.dashboardTitle = state.title;
      this.dashboardTitlePosition = state.position;
      this.dashboardTitleFontSize = state.fontSize;
    });
    const isLoggedIn = await this.keycloakService.isLoggedIn();
    if (isLoggedIn) {
      const userDetails = await this.keycloakService.loadUserProfile();
      this.username =
        `${userDetails?.firstName} ${userDetails?.lastName}`.trim() || 'user';
    }
    this.loadSidenavConfig();
  }
  async logout(): Promise<void> {
    await this.keycloakService.logout(window.location.origin);
    localStorage.clear();
  }

  loadSidenavConfig() {
    this.http
      .get<any>('assets/side-navbar-config/sidenav.config.json')
      .subscribe((data) => {
        const menuItems = Array.isArray(data) ? data : data?.menuItems;
        this.menuItems = Array.isArray(menuItems) ? menuItems : [];
        this.applyBreadcrumbFlowConfig(data);
        this.syncBreadcrumbs();
      });
  }

  navigateToBreadcrumb(item: { route: string }): void {
    const targetRoute = String(item?.route || '').trim();
    if (!targetRoute || targetRoute === this.normalizeRoutePath(this.router.url)) {
      return;
    }
    const flowItem = this.findBreadcrumbFlowItem(targetRoute, String((item as any)?.configFile || '').trim());
    const navigationState = this.buildBreadcrumbNavigationState(
      targetRoute,
      flowItem?.backNavigation || null,
    );

    if (navigationState) {
      this.router.navigate([targetRoute], {
        ...(navigationState.queryParams ? { queryParams: navigationState.queryParams } : {}),
        ...(navigationState.state ? { state: navigationState.state } : {}),
      });
      return;
    }

    this.router.navigateByUrl(targetRoute);
  }

  onMenuNavigation(item: any): void {
    const targetRoute = this.normalizeRoutePath(item?.path);
    if (!targetRoute || !this.breadcrumbFlowEnabled) {
      return;
    }

    const matchedFlow = this.breadcrumbFlows.find((flow) =>
      this.normalizeRoutePath(flow.items[0]?.path) === targetRoute,
    );
    if (matchedFlow?.key) {
      this.persistActiveFlowKey(matchedFlow.key);
    }
  }

  isLastBreadcrumb(index: number): boolean {
    return index === this.breadcrumbItems.length - 1;
  }

  toggleSidebar() {
    if (this.isMobileLayout) {
      this.isMobileDrawerOpen = !this.isMobileDrawerOpen;
      return;
    }
    this.isCollapsed = !this.isCollapsed;
    this.scheduleDesktopReflow();
  }

  /**
   * Heavy dashboards (like all-charts) may instantiate/defer widgets around the
   * sidebar transition window. Fire staged reflows so late-mounted widgets also
   * recalculate dimensions after expand/collapse.
   */
  protected scheduleDesktopReflow(): void {
    // Immediate pass
    this.responsiveLayoutService.triggerReflow();

    // During transition
    setTimeout(() => this.responsiveLayoutService.triggerReflow(), 180);

    // After transition end
    setTimeout(() => {
      this.responsiveLayoutService.triggerReflow();
      window.dispatchEvent(new Event('resize'));
    }, 360);
  }

  private loadBreadcrumbs(): void {
    try {
      const raw = sessionStorage.getItem(SideNavbarComponent.BREADCRUMB_STORAGE_KEY);
      if (!raw) {
        this.breadcrumbItems = [];
        return;
      }

      const parsed = JSON.parse(raw);
      this.breadcrumbItems = Array.isArray(parsed)
        ? parsed
            .map((item: any) => ({
              route: String(item?.route || '').trim(),
              configFile: String(item?.configFile || '').trim(),
              label: String(item?.label || '').trim(),
            }))
            .filter((item: any) => item.route && item.label)
        : [];
    } catch {
      this.breadcrumbItems = [];
    }
  }

  private persistBreadcrumbs(): void {
    try {
      sessionStorage.setItem(
        SideNavbarComponent.BREADCRUMB_STORAGE_KEY,
        JSON.stringify(this.breadcrumbItems),
      );
    } catch {
      /* ignore */
    }
  }

  private syncBreadcrumbs(): void {
    if (this.breadcrumbFlowEnabled) {
      this.syncBreadcrumbsFromConfiguredFlow();
      return;
    }

    const current = this.resolveCurrentBreadcrumb();
    if (!current) {
      return;
    }

    const existingIndex = this.breadcrumbItems.findIndex((item) => item.route === current.route);
    if (existingIndex === -1) {
      this.breadcrumbItems = [...this.breadcrumbItems, current];
    } else {
      this.breadcrumbItems = [
        ...this.breadcrumbItems.slice(0, existingIndex),
        current,
      ];
    }

    this.persistBreadcrumbs();
  }

  private syncBreadcrumbsFromConfiguredFlow(): void {
    const currentRoute = this.normalizeRoutePath(this.router.url);
    const currentConfigFile = this.resolveCurrentRouteConfigFile();
    if (!currentRoute) {
      this.breadcrumbItems = [];
      this.persistBreadcrumbs();
      return;
    }

    const navigationFlowKey = String(window.history?.state?.breadcrumbFlowKey || '').trim();
    const activeFlowKey = navigationFlowKey || this.loadActiveFlowKey();
    const activeFlow = activeFlowKey
      ? this.breadcrumbFlows.find((flow) => flow.key === activeFlowKey) ?? null
      : null;
    const currentFlow = this.resolveBreadcrumbFlow(currentRoute, currentConfigFile, activeFlow);

    if (!currentFlow) {
      this.breadcrumbItems = [];
      this.persistBreadcrumbs();
      this.clearActiveFlowKey();
      return;
    }

    this.persistActiveFlowKey(currentFlow.key);
    const currentIndex = this.findFlowItemIndex(currentFlow.items, currentRoute, currentConfigFile);
    if (currentIndex <= 0) {
      this.breadcrumbItems = [];
      this.persistBreadcrumbs();
      return;
    }

    this.breadcrumbItems = currentFlow.items
      .slice(0, currentIndex + 1)
      .map((item) => ({
        route: this.normalizeRoutePath(item.path),
        configFile: item.configFile,
        label: String(item.name || '').trim(),
      }))
      .filter((item) => item.route && item.label);
    this.persistBreadcrumbs();
  }

  private resolveCurrentBreadcrumb(): { route: string; configFile: string; label: string } | null {
    const snapshot = this.getDeepestRouteSnapshot(this.router.routerState.snapshot.root);
    const configFile = String(snapshot?.data?.['configFile'] || '').trim();
    const route = this.normalizeRoutePath(this.router.url);

    if (!configFile || !route) {
      return null;
    }

    return {
      route,
      configFile,
      label: this.extractConfigFileLabel(configFile),
    };
  }

  private getDeepestRouteSnapshot(snapshot: ActivatedRouteSnapshot | null): ActivatedRouteSnapshot | null {
    let current = snapshot;
    while (current?.firstChild) {
      current = current.firstChild;
    }
    return current;
  }

  private normalizeRoutePath(url: string): string {
    const clean = String(url || '').split('?')[0].split('#')[0].trim();
    if (!clean) {
      return '';
    }
    return clean.startsWith('/') ? clean : `/${clean}`;
  }

  private extractConfigFileLabel(configFile: string): string {
    const normalized = String(configFile || '').replace(/^\/+/, '').trim();
    if (!normalized) {
      return '';
    }
    const segments = normalized.split('/');
    return segments[segments.length - 1] || normalized;
  }

  private applyBreadcrumbFlowConfig(config: any): void {
    const flowConfig = config?.breadcrumbFlowConfig;
    this.breadcrumbFlowEnabled = flowConfig?.enabled === true;
    this.breadcrumbFlows = Array.isArray(flowConfig?.flows)
      ? flowConfig.flows
          .map((flow: any) => ({
            key: String(flow?.key || '').trim(),
            enabled: flow?.enabled !== false,
            items: Array.isArray(flow?.items)
              ? flow.items
                  .map((item: any) => ({
                    name: String(item?.name || '').trim(),
                    path: this.normalizeRoutePath(item?.path),
                    configFile: String(item?.configFile || this.resolveRouteConfigFile(item?.path) || '').trim(),
                    backNavigation: item?.backNavigation
                      ? {
                          requestParamKeys: this.normalizeStringArray(
                            item.backNavigation.requestParamKeys ?? item.backNavigation.requestDataKeys,
                          ),
                          queryParamKeys: this.normalizeStringArray(item.backNavigation.queryParamKeys),
                          navigationRequestParamKeys: this.normalizeStringArray(
                            item.backNavigation.navigationRequestParamKeys,
                          ),
                          navigationFilterListener: this.normalizeStringArray(
                            item.backNavigation.navigationFilterListener,
                          ),
                          navigationSourceWidgetId: String(
                            item.backNavigation.navigationSourceWidgetId || '',
                          ).trim(),
                          valueSource: this.normalizeBackNavigationValueSource(
                            item.backNavigation.valueSource,
                          ),
                          payloadMode: this.normalizeBackNavigationPayloadMode(
                            item.backNavigation.payloadMode,
                          ),
                        }
                      : null,
                  }))
                  .filter((item: any) => item.name && item.path)
              : [],
          }))
          .filter((flow: any) => flow.key && flow.enabled && flow.items.length)
      : [];
  }

  private resolveBreadcrumbFlow(
    currentRoute: string,
    currentConfigFile: string,
    preferredFlow: { key: string; enabled: boolean; items: Array<{ name: string; path: string; configFile: string }> } | null,
  ): { key: string; enabled: boolean; items: Array<{ name: string; path: string; configFile: string }> } | null {
    if (preferredFlow && this.findFlowItemIndex(preferredFlow.items, currentRoute, currentConfigFile) !== -1) {
      return preferredFlow;
    }

    return this.breadcrumbFlows.find((flow) =>
      this.findFlowItemIndex(flow.items, currentRoute, currentConfigFile) !== -1,
    ) ?? null;
  }

  private findFlowItemIndex(
    items: Array<{ name: string; path: string; configFile: string }>,
    currentRoute: string,
    currentConfigFile: string,
  ): number {
    const exactRouteIndex = items.findIndex((item) => this.normalizeRoutePath(item?.path) === currentRoute);
    if (exactRouteIndex !== -1) {
      return exactRouteIndex;
    }

    if (!currentConfigFile) {
      return -1;
    }

    return items.findIndex((item) => {
      const itemConfigFile = String(item?.configFile || '').trim();
      return !!itemConfigFile && itemConfigFile === currentConfigFile;
    });
  }

  private isFlowItemMatch(
    item: { name: string; path: string; configFile: string },
    currentRoute: string,
    currentConfigFile: string,
  ): boolean {
    const itemRoute = this.normalizeRoutePath(item?.path);
    const itemConfigFile = String(item?.configFile || '').trim();
    if (itemRoute === currentRoute) {
      return true;
    }
    if (itemConfigFile && currentConfigFile) {
      return itemConfigFile === currentConfigFile;
    }
    return false;
  }

  private resolveCurrentRouteConfigFile(): string {
    const snapshot = this.getDeepestRouteSnapshot(this.router.routerState.snapshot.root);
    return String(snapshot?.data?.['configFile'] || '').trim();
  }

  private resolveRouteConfigFile(path: string): string {
    const normalizedPath = this.normalizeRoutePath(path).replace(/^\/+/, '');
    if (!normalizedPath) {
      return '';
    }

    const matchedRoute = this.router.config.find((route) => String(route?.path || '').trim() === normalizedPath);
    return String(matchedRoute?.data?.['configFile'] || '').trim();
  }

  private loadActiveFlowKey(): string {
    try {
      return String(sessionStorage.getItem(SideNavbarComponent.BREADCRUMB_ACTIVE_FLOW_STORAGE_KEY) || '').trim();
    } catch {
      return '';
    }
  }

  private persistActiveFlowKey(flowKey: string): void {
    try {
      sessionStorage.setItem(SideNavbarComponent.BREADCRUMB_ACTIVE_FLOW_STORAGE_KEY, flowKey);
    } catch {
      /* ignore */
    }
  }

  private clearActiveFlowKey(): void {
    try {
      sessionStorage.removeItem(SideNavbarComponent.BREADCRUMB_ACTIVE_FLOW_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  private findBreadcrumbFlowItem(
    route: string,
    configFile: string,
  ): {
    name: string;
    path: string;
    configFile: string;
    backNavigation?: {
      requestParamKeys?: string[];
      queryParamKeys?: string[];
      navigationRequestParamKeys?: string[];
      navigationFilterListener?: string[];
      navigationSourceWidgetId?: string;
    } | null;
  } | null {
    const normalizedRoute = this.normalizeRoutePath(route);
    const normalizedConfigFile = String(configFile || '').trim();

    for (const flow of this.breadcrumbFlows) {
      const matchedItem = flow.items.find((candidate) => {
        const candidateRoute = this.normalizeRoutePath(candidate?.path);
        const candidateConfigFile = String(candidate?.configFile || '').trim();
        return (
          candidateRoute === normalizedRoute ||
          (!!normalizedConfigFile && !!candidateConfigFile && candidateConfigFile === normalizedConfigFile)
        );
      });

      if (matchedItem) {
        return matchedItem;
      }
    }

    return null;
  }

  private buildBreadcrumbNavigationState(
    targetRoute: string,
    backNavigation: {
      requestParamKeys?: string[];
      queryParamKeys?: string[];
      navigationRequestParamKeys?: string[];
      navigationFilterListener?: string[];
      navigationSourceWidgetId?: string;
      valueSource?: 'current' | 'target' | 'merged';
      payloadMode?: 'mapped' | 'savedState';
    } | null,
  ): { queryParams?: Record<string, any>; state?: Record<string, any> } | null {
    if (!backNavigation) {
      return null;
    }

    const currentState = this.getCurrentBreadcrumbPayloadContext();
    const targetSavedState = this.loadRoutePayload(targetRoute);
    if (targetRoute === '/dvm/deepdive') {
      console.log('[Breadcrumb Debug][navigate][deepdive]', {
        backNavigation,
        currentState,
        targetSavedState,
      });
    }
    if (backNavigation.payloadMode === 'savedState') {
      if (!targetSavedState || typeof targetSavedState !== 'object') {
        return null;
      }
      const filteredSavedState = this.filterSavedBreadcrumbState(targetSavedState, backNavigation);
      if (targetRoute === '/dvm/deepdive') {
        console.log('[Breadcrumb Debug][navigate][deepdive][filteredSavedState]', filteredSavedState);
      }
      const savedQueryParams =
        filteredSavedState?.['queryParams'] && Object.keys(filteredSavedState['queryParams']).length
          ? filteredSavedState['queryParams']
          : undefined;
      return {
        ...(savedQueryParams ? { queryParams: savedQueryParams } : {}),
        state: filteredSavedState,
      };
    }
    const sourceOrder = this.resolveBackNavigationSources(
      backNavigation.valueSource,
      currentState,
      targetSavedState,
    );

    const queryParams = this.pickMergedObjectKeys(
      sourceOrder.map((source) => source?.queryParams),
      backNavigation.queryParamKeys,
    );
    const requestParams = this.pickMergedObjectKeys(
      sourceOrder.map((source) => source?.requestParams),
      backNavigation.requestParamKeys,
    );
    const navigationRequestParams = this.pickMergedObjectKeys(
      sourceOrder.map((source) => source?.navigationRequestParams),
      backNavigation.navigationRequestParamKeys,
    );
    const navigationFilterListener = backNavigation.navigationFilterListener?.length
      ? [...backNavigation.navigationFilterListener]
      : this.normalizeStringArray(
          sourceOrder.map((source) => source?.navigationFilterListener).find((value) => value),
        );
    const navigationSourceWidgetId = String(
      backNavigation.navigationSourceWidgetId ||
        sourceOrder.map((source) => source?.navigationSourceWidgetId).find((value) => value) ||
        '',
    ).trim();

    const state: Record<string, any> = {};

    if (Object.keys(requestParams).length || Object.keys(queryParams).length) {
      state['crossNavPayload'] = {
        ...(Object.keys(requestParams).length ? { requestData: requestParams, requestParams } : {}),
        ...(Object.keys(queryParams).length ? { queryParams } : {}),
      };
    }

    if (Object.keys(requestParams).length) {
      state['requestParams'] = requestParams;
    }
    if (Object.keys(queryParams).length) {
      state['queryParams'] = queryParams;
    }
    if (Object.keys(navigationRequestParams).length) {
      state['navigationRequestParams'] = navigationRequestParams;
    }
    if (navigationFilterListener?.length) {
      state['navigationFilterListener'] = navigationFilterListener;
    }
    if (navigationSourceWidgetId) {
      state['navigationSourceWidgetId'] = navigationSourceWidgetId;
    }

    if (!Object.keys(state).length) {
      return null;
    }

    return {
      ...(Object.keys(queryParams).length ? { queryParams } : {}),
      state,
    };
  }

  private getCurrentBreadcrumbPayloadContext(): {
    queryParams?: Record<string, any>;
    requestParams?: Record<string, any>;
    navigationRequestParams?: Record<string, any>;
    navigationFilterListener?: string[] | string;
    navigationSourceWidgetId?: string;
  } | null {
    const currentRoute = this.normalizeRoutePath(this.router.url);
    const savedCurrentState = currentRoute ? this.loadRoutePayload(currentRoute) : null;
    const historyState = (window.history?.state || {}) as Record<string, any>;
    const crossNavPayload =
      historyState?.['crossNavPayload'] && typeof historyState['crossNavPayload'] === 'object'
        ? historyState['crossNavPayload']
        : null;

    return {
      queryParams: {
        ...(savedCurrentState?.queryParams || {}),
        ...(historyState?.['queryParams'] || {}),
        ...(crossNavPayload?.['queryParams'] || {}),
      },
      requestParams: {
        ...(savedCurrentState?.requestParams || {}),
        ...(historyState?.['requestParams'] || {}),
        ...(crossNavPayload?.['requestParams'] || {}),
        ...(crossNavPayload?.['requestData'] || {}),
      },
      navigationRequestParams: {
        ...(savedCurrentState?.navigationRequestParams || {}),
        ...(historyState?.['navigationRequestParams'] || {}),
      },
      navigationFilterListener:
        historyState?.['navigationFilterListener'] ??
        savedCurrentState?.navigationFilterListener,
      navigationSourceWidgetId: String(
        historyState?.['navigationSourceWidgetId'] ||
          savedCurrentState?.navigationSourceWidgetId ||
          '',
      ).trim(),
    };
  }

  private loadRoutePayload(route: string): any | null {
    const normalizedRoute = this.normalizeRoutePath(route);
    if (!normalizedRoute) {
      return null;
    }

    const routeStateMap = this.loadAllRoutePayloads();
    return routeStateMap[normalizedRoute] ?? null;
  }

  private loadAllRoutePayloads(): Record<string, any> {
    try {
      const raw = sessionStorage.getItem(SideNavbarComponent.BREADCRUMB_ROUTE_STATE_STORAGE_KEY);
      if (!raw) {
        return {};
      }

      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private pickObjectKeys(source: any, keys?: string[]): Record<string, any> {
    const normalizedKeys = this.normalizeStringArray(keys);
    if (!source || typeof source !== 'object' || !normalizedKeys.length) {
      return {};
    }

    return normalizedKeys.reduce((acc: Record<string, any>, key: string) => {
      if (Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined) {
        acc[key] = source[key];
      }
      return acc;
    }, {});
  }

  private pickMergedObjectKeys(sources: any[], keys?: string[]): Record<string, any> {
    const normalizedKeys = this.normalizeStringArray(keys);
    if (!normalizedKeys.length) {
      return {};
    }

    return normalizedKeys.reduce((acc: Record<string, any>, key: string) => {
      for (const source of sources) {
        if (source && typeof source === 'object' && Object.prototype.hasOwnProperty.call(source, key)) {
          const value = source[key];
          if (value !== undefined) {
            acc[key] = value;
            break;
          }
        }
      }
      return acc;
    }, {});
  }

  private normalizeBackNavigationValueSource(value: unknown): 'current' | 'target' | 'merged' {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'current' || normalized === 'target') {
      return normalized;
    }
    return 'merged';
  }

  private normalizeBackNavigationPayloadMode(value: unknown): 'mapped' | 'savedState' {
    return String(value || '').trim().toLowerCase() === 'savedstate'
      ? 'savedState'
      : 'mapped';
  }

  private filterSavedBreadcrumbState(
    savedState: Record<string, any>,
    backNavigation: {
      requestParamKeys?: string[];
      queryParamKeys?: string[];
      navigationRequestParamKeys?: string[];
      navigationFilterListener?: string[];
      navigationSourceWidgetId?: string;
      valueSource?: 'current' | 'target' | 'merged';
      payloadMode?: 'mapped' | 'savedState';
    },
  ): Record<string, any> {
    const clonedState = this.cloneObject(savedState);
    const requestParamSource =
      Object.keys(clonedState?.['requestParams'] || {}).length
        ? clonedState?.['requestParams']
        : Object.keys(clonedState?.['initialRequestParams'] || {}).length
          ? clonedState?.['initialRequestParams']
          : Object.keys(clonedState?.['navigationRequestParams'] || {}).length
            ? clonedState?.['navigationRequestParams']
            : clonedState?.['crossNavPayload']?.['requestParams'] ||
              clonedState?.['crossNavPayload']?.['requestData'];
    const requestParams = this.pickObjectKeys(
      requestParamSource,
      backNavigation.requestParamKeys,
    );
    const queryParamSource =
      Object.keys(clonedState?.['queryParams'] || {}).length
        ? clonedState?.['queryParams']
        : Object.keys(clonedState?.['initialQueryParams'] || {}).length
          ? clonedState?.['initialQueryParams']
          : clonedState?.['crossNavPayload']?.['queryParams'];
    const queryParams = this.pickObjectKeys(
      queryParamSource,
      backNavigation.queryParamKeys,
    );
    const navigationRequestParams = this.pickObjectKeys(
      clonedState?.['navigationRequestParams'],
      backNavigation.navigationRequestParamKeys,
    );

    if (Object.keys(requestParams).length) {
      clonedState['requestParams'] = requestParams;
      clonedState['initialRequestParams'] = requestParams;
    } else {
      delete clonedState['requestParams'];
      delete clonedState['initialRequestParams'];
    }

    if (Object.keys(queryParams).length) {
      clonedState['queryParams'] = queryParams;
      clonedState['initialQueryParams'] = queryParams;
    } else {
      delete clonedState['queryParams'];
      delete clonedState['initialQueryParams'];
    }

    if (Object.keys(navigationRequestParams).length) {
      clonedState['navigationRequestParams'] = navigationRequestParams;
    } else {
      delete clonedState['navigationRequestParams'];
    }

    const crossNavPayload = clonedState?.['crossNavPayload'];
    if (crossNavPayload && typeof crossNavPayload === 'object') {
      const filteredCrossNavPayload: Record<string, any> = {};
      if (Object.keys(requestParams).length) {
        filteredCrossNavPayload['requestData'] = requestParams;
        filteredCrossNavPayload['requestParams'] = requestParams;
      }
      if (Object.keys(queryParams).length) {
        filteredCrossNavPayload['queryParams'] = queryParams;
      }
      if (Object.keys(filteredCrossNavPayload).length) {
        clonedState['crossNavPayload'] = filteredCrossNavPayload;
      } else {
        delete clonedState['crossNavPayload'];
      }
    }

    return clonedState;
  }

  private cloneObject<T>(value: T): T {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value;
    }
  }

  private resolveBackNavigationSources(
    valueSource: 'current' | 'target' | 'merged' | undefined,
    currentState: any,
    targetSavedState: any,
  ): any[] {
    switch (valueSource) {
      case 'current':
        return [currentState];
      case 'target':
        return [targetSavedState];
      default:
        return [currentState, targetSavedState];
    }
  }

  private normalizeStringArray(value: unknown): string[] {
    const rawList = Array.isArray(value)
      ? value
      : typeof value === 'string'
        ? value.split(',')
        : [];

    return rawList
      .map((item) => String(item || '').trim())
      .filter((item) => !!item);
  }

  mouseenter() {
    if (!this.isExpanded) {
      this.isShowing = true;
    }
  }
  mouseleave() {
    if (!this.isExpanded) {
      this.isShowing = false;
    }
  }
  goToHelp() {
    const helpUrl = environment.HELP_URL || 'https://default.help.com';
    window.open(helpUrl, '_blank');
  }

  goToFeedback() {
    const feedbackUrl = environment.FEEDBACK_URL;
    if (feedbackUrl) window.open(feedbackUrl, '_blank');
  }

  showNotifications() {
    alert('Notifications clicked!');
  }

  openThemeSelector(): void {
    // Re-clicking the Theme Options button while the dialog is open used to
    // stack a second instance on top of the first. Guard against that by
    // tracking the live ref and bailing out if it's still open.
    if (this.themeSelectorDialogRef) {
      this.themeSelectorDialogRef.addPanelClass('dvm-theme-selector-pulse');
      return;
    }
    this.themeSelectorDialogRef = this.dialog.open(ThemeSelectorComponent, {
      panelClass: 'dvm-theme-selector-panel',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: false,
    });
    this.themeSelectorDialogRef.afterClosed().subscribe(() => {
      this.themeSelectorDialogRef = null;
    });
  }

  toggleSubmenu(menuName: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.expandedMenus.has(menuName)) {
      this.expandedMenus.delete(menuName);
    } else {
      this.expandedMenus.add(menuName);
    }
  }

  isMenuExpanded(menuName: string): boolean {
    return this.expandedMenus.has(menuName);
  }

  isActive(item: any): boolean {
    try {
      if (!item || !item.path) return false;
      return this.router.url.includes(item.path);
    } catch (e) {
      return false;
    }
  }

  canShowMacroShortcut(): boolean {
    return true;
  }

  toggleMacroPanel(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.isMacroPanelOpen = !this.isMacroPanelOpen;
  }

  selectMacro(macro: string): void {
    // Minimal selection handler — close panel and store selection
    this.isMacroPanelOpen = false;
    try {
      localStorage.setItem('selectedMacro', macro);
    } catch (e) {
      // ignore
    }
    console.log('Macro selected:', macro);
  }

  hasChildren(item: any): boolean {
    return item.children && item.children.length > 0;
  }
}
